import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const SpectrumAnalyzer = ({ audioData, vibeConfig }) => {
  const spectrumRef = useRef();
  const waveformRef = useRef();
  
  const barCount = 64; // Number of frequency bars
  const waveformPoints = 128; // Number of waveform points

  // Create spectrum bars
  const spectrum = useMemo(() => {
    const positions = [];
    const spectrumColors = [];
    const indices = [];
    
    for (let i = 0; i < barCount; i++) {
      const x = (i - barCount / 2) * 0.3;
      const z = 0;
      
      // Create quad for each bar (2 triangles)
      const baseY = -5;
      
      // Bottom left, bottom right, top right, top left
      positions.push(
        x - 0.12, baseY, z,      // 0
        x + 0.12, baseY, z,      // 1
        x + 0.12, baseY + 1, z,  // 2
        x - 0.12, baseY + 1, z   // 3
      );
      
      // Create gradient colors for each bar
      const hue = (i / barCount) * 360; // Rainbow across spectrum
      const color1 = new THREE.Color().setHSL(hue / 360, 0.8, 0.3); // Bottom color
      const color2 = new THREE.Color().setHSL(hue / 360, 1.0, 0.7); // Top color
      
      // Bottom vertices (darker)
      spectrumColors.push(color1.r, color1.g, color1.b);
      spectrumColors.push(color1.r, color1.g, color1.b);
      // Top vertices (brighter)
      spectrumColors.push(color2.r, color2.g, color2.b);
      spectrumColors.push(color2.r, color2.g, color2.b);
      
      // Indices for two triangles per bar
      const baseIndex = i * 4;
      indices.push(
        baseIndex, baseIndex + 1, baseIndex + 2,
        baseIndex, baseIndex + 2, baseIndex + 3
      );
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(spectrumColors),
      indices: new Uint16Array(indices)
    };
  }, [barCount]);

  // Create waveform line
  const waveform = useMemo(() => {
    const positions = [];
    const waveColors = [];
    
    for (let i = 0; i < waveformPoints; i++) {
      const x = (i - waveformPoints / 2) * 0.15;
      const y = 2; // Base height for waveform
      const z = 0;
      
      positions.push(x, y, z);
      
      // Gradient colors across waveform
      const hue = (i / waveformPoints) * 300 + 180; // Cyan to magenta
      const color = new THREE.Color().setHSL(hue / 360, 1.0, 0.6);
      waveColors.push(color.r, color.g, color.b);
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(waveColors)
    };
  }, [waveformPoints]);

  useFrame(() => {
    if (!audioData) return;

    // Update spectrum bars
    if (spectrumRef.current) {
      const positions = spectrumRef.current.geometry.attributes.position.array;
      const colors = spectrumRef.current.geometry.attributes.color.array;
      
      for (let i = 0; i < barCount; i++) {
        // Get frequency data for this bar
        const freqIndex = Math.floor((i / barCount) * audioData.frequency.length);
        const amplitude = audioData.frequency[freqIndex] || 0;
        
        // Scale amplitude for visual effect
        const height = amplitude * 12 + 0.2; // Increased height for more dramatic effect
        
        // Update top vertices of the bar
        const baseIndex = i * 4;
        positions[baseIndex * 3 + 7] = -5 + height; // Top left Y
        positions[baseIndex * 3 + 10] = -5 + height; // Top right Y
        
        // Dynamic color intensity based on amplitude
        const intensity = 0.4 + amplitude * 3; // Increased intensity
        const hue = (i / barCount) * 360;
        
        // Update colors with intensity
        const color1 = new THREE.Color().setHSL(hue / 360, 0.9, Math.min(intensity * 0.4, 0.6)); // More saturated
        const color2 = new THREE.Color().setHSL(hue / 360, 1.0, Math.min(intensity * 0.8, 1.0)); // Brighter top
        
        // Bottom vertices
        colors[baseIndex * 3] = color1.r;
        colors[baseIndex * 3 + 1] = color1.g;
        colors[baseIndex * 3 + 2] = color1.b;
        colors[baseIndex * 3 + 3] = color1.r;
        colors[baseIndex * 3 + 4] = color1.g;
        colors[baseIndex * 3 + 5] = color1.b;
        
        // Top vertices
        colors[baseIndex * 3 + 6] = color2.r;
        colors[baseIndex * 3 + 7] = color2.g;
        colors[baseIndex * 3 + 8] = color2.b;
        colors[baseIndex * 3 + 9] = color2.r;
        colors[baseIndex * 3 + 10] = color2.g;
        colors[baseIndex * 3 + 11] = color2.b;
      }
      
      spectrumRef.current.geometry.attributes.position.needsUpdate = true;
      spectrumRef.current.geometry.attributes.color.needsUpdate = true;
    }

    // Update waveform
    if (waveformRef.current) {
      const positions = waveformRef.current.geometry.attributes.position.array;
      const colors = waveformRef.current.geometry.attributes.color.array;
      
      for (let i = 0; i < waveformPoints; i++) {
        // Get waveform data
        const freqIndex = Math.floor((i / waveformPoints) * audioData.frequency.length);
        const amplitude = audioData.frequency[freqIndex] || 0;
        
        // Update Y position for waveform
        positions[i * 3 + 1] = 2 + Math.sin(i * 0.1) * amplitude * 3;
        
        // Dynamic colors
        const intensity = 0.5 + amplitude * 1.5;
        const hue = (i / waveformPoints) * 300 + 180;
        const color = new THREE.Color().setHSL(hue / 360, 1.0, Math.min(intensity, 1.0));
        
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      }
      
      waveformRef.current.geometry.attributes.position.needsUpdate = true;
      waveformRef.current.geometry.attributes.color.needsUpdate = true;
    }
  });

  return (
    <group position={[0, -6, 0]}> {/* Moved up slightly for better visibility */}
      {/* Spectrum bars */}
      <mesh ref={spectrumRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={spectrum.positions.length / 3}
            array={spectrum.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={spectrum.colors.length / 3}
            array={spectrum.colors}
            itemSize={3}
          />
          <bufferAttribute
            attach="index"
            count={spectrum.indices.length}
            array={spectrum.indices}
            itemSize={1}
          />
        </bufferGeometry>
        <meshStandardMaterial 
          vertexColors 
          transparent 
          opacity={0.9}
          emissiveIntensity={audioData ? audioData.average * 0.2 : 0}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Waveform line */}
      <line ref={waveformRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={waveform.positions.length / 3}
            array={waveform.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={waveform.colors.length / 3}
            array={waveform.colors}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial 
          vertexColors 
          transparent 
          opacity={0.8}
          linewidth={3}
        />
      </line>

      {/* Circular spectrum */}
      <CircularSpectrum audioData={audioData} />
      
      {/* Frequency rings */}
      <FrequencyRings audioData={audioData} />
    </group>
  );
};

// Circular spectrum component
const CircularSpectrum = ({ audioData }) => {
  const circularRef = useRef();
  const barCount = 48;

  const circular = useMemo(() => {
    const positions = [];
    const colors = [];
    const indices = [];
    
    for (let i = 0; i < barCount; i++) {
      const angle = (i / barCount) * Math.PI * 2;
      const innerRadius = 3;
      const outerRadius = 4;
      
      // Create radial bar
      const x1 = Math.cos(angle) * innerRadius;
      const z1 = Math.sin(angle) * innerRadius;
      const x2 = Math.cos(angle) * outerRadius;
      const z2 = Math.sin(angle) * outerRadius;
      
      // Bar vertices
      positions.push(
        x1, -3, z1,  // Inner bottom
        x2, -3, z2,  // Outer bottom  
        x2, -2, z2,  // Outer top
        x1, -2, z1   // Inner top
      );
      
      // Rainbow colors
      const hue = (i / barCount) * 360;
      const color = new THREE.Color().setHSL(hue / 360, 1.0, 0.6);
      
      for (let j = 0; j < 4; j++) {
        colors.push(color.r, color.g, color.b);
      }
      
      // Indices
      const baseIndex = i * 4;
      indices.push(
        baseIndex, baseIndex + 1, baseIndex + 2,
        baseIndex, baseIndex + 2, baseIndex + 3
      );
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(colors),
      indices: new Uint16Array(indices)
    };
  }, [barCount]);

  useFrame(() => {
    if (!audioData || !circularRef.current) return;

    const positions = circularRef.current.geometry.attributes.position.array;
    const colors = circularRef.current.geometry.attributes.color.array;
    
    for (let i = 0; i < barCount; i++) {
      const freqIndex = Math.floor((i / barCount) * audioData.frequency.length);
      const amplitude = audioData.frequency[freqIndex] || 0;
      
      const angle = (i / barCount) * Math.PI * 2;
      const outerRadius = 4 + amplitude * 3;
      
      // Update outer vertices
      const baseIndex = i * 4;
      const x2 = Math.cos(angle) * outerRadius;
      const z2 = Math.sin(angle) * outerRadius;
      
      positions[baseIndex * 3 + 3] = x2; // Outer bottom X
      positions[baseIndex * 3 + 5] = z2; // Outer bottom Z
      positions[baseIndex * 3 + 6] = x2; // Outer top X
      positions[baseIndex * 3 + 8] = z2; // Outer top Z
      
      // Dynamic colors
      const intensity = 0.4 + amplitude * 1.5;
      const hue = (i / barCount) * 360;
      const color = new THREE.Color().setHSL(hue / 360, 1.0, Math.min(intensity, 1.0));
      
      for (let j = 0; j < 4; j++) {
        colors[baseIndex * 3 + j * 3] = color.r;
        colors[baseIndex * 3 + j * 3 + 1] = color.g;
        colors[baseIndex * 3 + j * 3 + 2] = color.b;
      }
    }
    
    circularRef.current.geometry.attributes.position.needsUpdate = true;
    circularRef.current.geometry.attributes.color.needsUpdate = true;
  });

  return (
    <mesh ref={circularRef} position={[8, 0, 0]}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={circular.positions.length / 3}
          array={circular.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={circular.colors.length / 3}
          array={circular.colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="index"
          count={circular.indices.length}
          array={circular.indices}
          itemSize={1}
        />
      </bufferGeometry>
      <meshBasicMaterial 
        vertexColors 
        transparent 
        opacity={0.8}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

// Frequency rings component
const FrequencyRings = ({ audioData }) => {
  const ringsRef = useRef();
  const ringCount = 5;

  useFrame((state) => {
    if (!audioData || !ringsRef.current) return;

    const time = state.clock.getElapsedTime();
    
    ringsRef.current.children.forEach((ring, i) => {
      const freqBand = ['bass', 'mid', 'treble', 'peak', 'average'][i] || 'average';
      const amplitude = audioData[freqBand] || 0;
      
      const scale = 1 + amplitude * 2;
      ring.scale.setScalar(scale);
      
      ring.rotation.z = time * (0.2 + i * 0.1) + amplitude;
      
      // Dynamic color
      const hue = (i / ringCount) * 360 + time * 20;
      const color = new THREE.Color().setHSL(hue / 360, 1.0, 0.5 + amplitude * 0.5);
      ring.material.color = color;
      ring.material.opacity = 0.3 + amplitude * 0.4;
    });
  });

  return (
    <group ref={ringsRef} position={[-8, 0, 0]}>
      {Array.from({ length: ringCount }, (_, i) => (
        <mesh key={i} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[2 + i * 0.5, 0.05, 8, 32]} />
          <meshBasicMaterial 
            transparent 
            opacity={0.5}
            color="#00ffff"
          />
        </mesh>
      ))}
    </group>
  );
};

export default SpectrumAnalyzer;